import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {SchemaTag} from 'folder-items/FileConstants';
import notifications from 'notifications';
import * as TestOrderDAO from 'sample-tests/network/TestOrderDAO';
import {FieldType, isRequiredLinkStorableSchema, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';

export interface UpdateTestOrdersOnPlatesFieldModalProps {
  schemaFields: Array<SchemaField>;
  schemaTags: Array<SchemaTag>;
  runName: string;
  pmeiId: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void; // Fulfill runModal contract
}

export default function UpdateTestOrdersOnPlatesFieldModal(
  props: UpdateTestOrdersOnPlatesFieldModalProps
): JSX.Element {
  const possibleInputPlateFields = props.schemaFields.filter((f) => isPossibleInputPlateField(f));
  const [selectedFieldApiId, setSelectedFieldApiId] = useState<string | null>(null);
  const selectedField = possibleInputPlateFields.find(({apiId}) => apiId === selectedFieldApiId) ?? null;
  const selectedFieldValue = props.schemaTags.find(
    ({tagSchemaField}) => tagSchemaField.apiId === selectedFieldApiId
  );
  const plateIds =
    selectedFieldValue?.targetStorables?.map(({id}) => id).filter((id) => APIUtil.isPlate(id)) ?? [];

  return (
    <Modal
      title={<div className={css(styles.modalTitle)}>Update test order statuses in {props.runName}</div>}
      mediumLarge={true}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={async () => {
        await TestOrderDAO.workaroundProgressTestOrdersOnPlatesForMethodInstance(plateIds, props.pmeiId);
        notifications.success(
          `Successfully updated test orders for "${selectedField?.displayName ?? ''}" plates on ${props.runName}`
        );
        props.onRequestSubmit();
      }}
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Update', submit: true, disabled: selectedField == null || plateIds.length === 0},
      ]}
      closeable={true}
      expandHeight={true}
    >
      {possibleInputPlateFields.length === 0 && (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={`No plate fields were found on ${props.runName}.`}
        />
      )}
      <FieldLabel isRequired={true} label="Specify plates" />
      <SelectInputPlatesFieldDropdown
        selectedField={selectedField}
        onSelectField={setSelectedFieldApiId}
        selectableFields={possibleInputPlateFields}
      />
      <div className={css(styles.modalText)}>
        You're about to update all test orders for every sample in these plates to In Progress. Do you want to
        continue?
      </div>
      {plateIds.length === 0 ? (
        selectedField != null && (
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={`No plates were found on the "${selectedField.displayName}" field.`}
          />
        )
      ) : (
        <ChipList itemIds={plateIds} isVertical={false} chipSize={ChipSize.SMALL} />
      )}
    </Modal>
  );
}

function SelectInputPlatesFieldDropdown(props: {
  selectedField: SchemaField | null;
  onSelectField: (fieldApiId: string) => void;
  selectableFields: Array<SchemaField>;
}): JSX.Element {
  const menu = (
    <Menu value={props.selectedField?.apiId}>
      {props.selectableFields.map((field, i) => (
        <Option
          onSelect={(fieldApiId: string): void => {
            props.onSelectField(fieldApiId);
          }}
          value={field.apiId}
          key={i}
        >
          {field.displayName}
        </Option>
      ))}
    </Menu>
  );
  return (
    <SelectDropdown displayName={props.selectedField?.displayName ?? 'Select plate field'} menu={menu} />
  );
}

function isPossibleInputPlateField(field: SchemaField): boolean {
  const isStorableField = field.fieldType === FieldType.STORABLE_LINK;
  const requiredSchemaId = isRequiredLinkStorableSchema(field.requiredLink)
    ? field.requiredLink.storableSchema.id
    : null;
  const allowsPlates = requiredSchemaId == null || APIUtil.isPlateSchema(requiredSchemaId);
  return isStorableField && allowsPlates;
}

const styles = StyleSheet.create({
  modalText: {
    fontSize: '13px',
    fontWeight: 400,
    paddingTop: '16px',
    paddingBottom: '16px',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 400,
  },
  modalColumnHeader: {
    fontSize: '13px',
    fontWeight: 600,
  },
  errorBanner: {
    marginBottom: '12px',
  },
});
